import React, { useState, useRef } from 'react';
import { X, Check, AlertCircle, ChevronDown, ChevronUp, Download } from 'lucide-react';
import Papa from 'papaparse';
import { downloadBlobFile } from '../../../utils/fileHelper';

const CustomRecipientsSection = ({ formData, handleInputChange, isDark, errors = {} }) => {
    const [csvData, setCsvData] = useState(formData.customRecipients || []);
    const [fileName, setFileName] = useState('');
    const [uploadStatus, setUploadStatus] = useState(null);
    const [uploadError, setUploadError] = useState('');
    const [importedCount, setImportedCount] = useState(0);
    const fileInputRef = useRef(null);

    const [manualName, setManualName] = useState('');
    const [manualPostal, setManualPostal] = useState('');
    const [manualError, setManualError] = useState('');

    const [showRecipients, setShowRecipients] = useState(false);

    const REQUIRED_COLUMNS = ['name', 'postal'];

    const processFile = (file) => {
        if (!file) return;

        setUploadStatus(null);
        setUploadError('');

        if (!file.name.endsWith('.csv')) {
            setUploadError('Please upload a CSV file');
            setUploadStatus('error');
            setFileName(file.name);
            return;
        }

        setFileName(file.name);

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                const { data, errors: parseErrors } = results;

                if (parseErrors.length > 0) {
                    setUploadError('Error parsing CSV file. Please check the format.');
                    setUploadStatus('error');
                    return;
                }

                if (data.length === 0) {
                    setUploadError('CSV file is empty');
                    setUploadStatus('error');
                    return;
                }

                const headers = Object.keys(data[0]);
                const missingColumns = REQUIRED_COLUMNS.filter(col => !headers.includes(col));

                if (missingColumns.length > 0) {
                    setUploadError(`"${missingColumns[0]}" is not found! Please match the fields and try again.`);
                    setUploadStatus('error');
                    return;
                }

                const validatedData = [];
                const validationErrors = [];

                data.forEach((row, index) => {
                    const rowNum = index + 1;

                    if (!row.name || !row.name.trim()) {
                        validationErrors.push(`Row ${rowNum}: name is required`);
                    }
                    if (!row.postal || !row.postal.trim()) {
                        validationErrors.push(`Row ${rowNum}: postal address is required`);
                    }

                    validatedData.push({
                        name: row.name?.trim() || '',
                        postal: row.postal?.trim() || '',
                        source: 'csv'
                    });
                });

                if (validationErrors.length > 0) {
                    setUploadError(validationErrors[0]);
                    setUploadStatus('error');
                    return;
                }

                const manualRecipients = csvData.filter(recipient => recipient.source === 'manual');
                const allRecipients = [...manualRecipients, ...validatedData];

                setCsvData(allRecipients);
                setImportedCount(validatedData.length);
                handleInputChange('customRecipients', allRecipients);
                setUploadStatus('success');
                setUploadError('');
            },
            error: (error) => {
                setUploadError('Error reading file: ' + error.message);
                setUploadStatus('error');
            }
        });
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        processFile(file);
    };

    const handleRemoveFile = () => {
        const manualRecipients = csvData.filter(recipient => recipient.source === 'manual');
        setCsvData(manualRecipients);
        setFileName('');
        setUploadStatus(null);
        setUploadError('');
        setImportedCount(0);
        handleInputChange('customRecipients', manualRecipients);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const clearStatus = () => {
        setUploadStatus(null);
        setUploadError('');
    };

    const handleManualSubmit = (e) => {
        e.preventDefault();
        setManualError('');

        if (!manualName.trim()) {
            setManualError('Name is required');
            return;
        }
        if (!manualPostal.trim()) {
            setManualError('Postal address is required');
            return;
        }

        const newRecipient = {
            name: manualName.trim(),
            postal: manualPostal.trim(),
            source: 'manual'
        };

        const updatedRecipients = [...csvData, newRecipient];
        setCsvData(updatedRecipients);
        handleInputChange('customRecipients', updatedRecipients);

        setManualName('');
        setManualPostal('');
        setManualError('');
    };

    const handleRemoveRecipient = (index) => {
        const updatedRecipients = csvData.filter((_, i) => i !== index);
        setCsvData(updatedRecipients);
        handleInputChange('customRecipients', updatedRecipients);

        if (updatedRecipients.length === 0) {
            setUploadStatus(null);
            setFileName('');
            setImportedCount(0);
        }
    };

    const downloadTemplate = () => {
        const template = `name,postal`;
        downloadBlobFile(template, 'recipients_template.csv', 'csv');
    };

    const downloadUploadedFile = () => {
        if (csvData.length === 0) return;
        const csv = Papa.unparse(csvData);
        downloadBlobFile(csv, fileName || 'recipients.csv', 'csv');
    };

    // Small CSV Icon Component (14x14)
    const SmallCsvIcon = () => (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10.7917 2.91667C10.7136 2.91667 10.5 2.70299 10.5 2.625V0H4.08333C3.16212 0 2.91667 0.523827 2.91667 1.16667V6.41667H10.9375C11.9886 6.41667 12.5417 7.13628 12.5417 8.02083V10.9375C12.5417 11.8221 11.9886 12.5417 10.9375 12.5417H2.91667V12.8333C2.91667 13.4762 3.16212 14 4.08333 14H12.25C13.1712 14 13.4167 13.4762 13.4167 12.8333V2.91667H10.7917ZM4.66667 1.75H7C7.16122 1.75 7.29167 1.88045 7.29167 2.04167C7.29167 2.20288 7.16122 2.33333 7 2.33333H4.66667C4.50545 2.33333 4.375 2.20288 4.375 2.04167C4.375 1.88045 4.50545 1.75 4.66667 1.75ZM9.33333 4.66667H4.66667C4.50545 4.66667 4.375 4.53622 4.375 4.375C4.375 4.21378 4.50545 4.08333 4.66667 4.08333H9.33333C9.49455 4.08333 9.625 4.21378 9.625 4.375C9.625 4.53622 9.49455 4.66667 9.33333 4.66667ZM9.33333 3.5H4.66667C4.50545 3.5 4.375 3.36955 4.375 3.20833C4.375 3.04712 4.50545 2.91667 4.66667 2.91667H9.33333C9.49455 2.91667 9.625 3.04712 9.625 3.20833C9.625 3.36955 9.49455 3.5 9.33333 3.5Z" fill="#0A6E29"/>
            <path d="M11.0833 0.170718V2.33334H13.246L11.0833 0.170718Z" fill="#0A6E29"/>
            <path d="M11.9583 10.9375V8.02083C11.9583 7.45733 11.501 7 10.9375 7C0.78652 7 1.44824 7.00066 1.41487 6.99876C1.10203 6.98892 0.816488 6.88231 0.583333 6.70467V10.3792C0.583333 11.2474 1.29423 11.9583 2.16252 11.9583H10.9375C11.501 11.9583 11.9583 11.501 11.9583 10.9375ZM4.52083 10.5C4.59859 10.5 4.64583 10.5474 4.64583 10.7917C4.64583 10.9529 4.59859 11.0833 4.52083 11.0833C3.71699 11.0833 3.0625 10.4018 3.0625 9.625C3.0625 8.84816 3.71699 8.16667 4.52083 8.16667C4.59859 8.16667 4.64583 8.21391 4.64583 8.45833C4.64583 8.61955 4.59859 8.75 4.52083 8.75C4.04015 8.75 3.64583 9.14432 3.64583 9.625C3.64583 10.1057 4.04015 10.5 4.52083 10.5ZM6.125 9.33333H6.41667C6.89735 9.33333 7.29167 9.72765 7.29167 10.2083C7.29167 10.689 6.89735 11.0833 6.41667 11.0833H5.54167C5.38045 11.0833 5.25 10.9529 5.25 10.7917C5.25 10.6305 5.38045 10.5 5.54167 10.5H6.41667C6.57453 10.5 6.70833 10.3662 6.70833 10.2083C6.70833 10.0505 6.57453 9.91667 6.41667 9.91667H6.125C5.64432 9.91667 5.25 9.52235 5.25 9.04167C5.25 8.56098 5.64432 8.16667 6.125 8.16667H7C7.16122 8.16667 7.29167 8.21391 7.29167 8.45833C7.29167 8.61955 7.16122 8.75 7 8.75H6.125C5.96715 8.75 5.83333 8.88382 5.83333 9.04167C5.83333 9.19952 5.96715 9.33333 6.125 9.33333ZM10.1995 8.5552L9.28703 10.9719C9.23762 11.1191 9.09842 11.0833 9.04167 11.0833C8.98491 11.0833 8.84571 11.1191 8.7963 10.9719L7.88384 8.5552C7.80923 8.32659 7.88546 8.17462 8.01428 8.07137C8.14343 7.96826 8.29222 8.03843 8.36683 8.267L9.04167 9.97212L9.7165 8.267C9.79111 8.03843 9.9399 7.96826 10.0691 8.07137C10.1979 8.17462 10.2741 8.32659 10.1995 8.5552Z" fill="#0A6E29"/>
            <path d="M1.45833 6.41667H2.33333V4.66667H1.45833C0.975407 4.66667 0.583333 5.02954 0.583333 5.54167C0.583333 6.05379 0.975407 6.41667 1.45833 6.41667Z" fill="#0A6E29"/>
        </svg>
    );

    // Large CSV Icon Component (16x16 for button)
    const LargeCsvIcon = () => (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12.3333 3.33333C12.2441 3.33333 12 3.08914 12 3V0H4.66667C3.61385 0 3.33333 0.598373 3.33333 1.33333V7.33333H12.5C13.7011 7.33333 14.3333 8.15575 14.3333 9.16667V12.5C14.3333 13.511 13.7011 14.3333 12.5 14.3333H3.33333V14.6667C3.33333 15.4013 3.61385 16 4.66667 16H14C15.0528 16 15.3333 15.4013 15.3333 14.6667V3.33333H12.3333ZM5.33333 2H8C8.18425 2 8.33333 2.14909 8.33333 2.33333C8.33333 2.51758 8.18425 2.66667 8 2.66667H5.33333C5.14909 2.66667 5 2.51758 5 2.33333C5 2.14909 5.14909 2 5.33333 2ZM10.6667 5.33333H5.33333C5.14909 5.33333 5 5.18425 5 5C5 4.81575 5.14909 4.66667 5.33333 4.66667H10.6667C10.8509 4.66667 11 4.81575 11 5C11 5.18425 10.8509 5.33333 10.6667 5.33333ZM10.6667 4H5.33333C5.14909 4 5 3.85091 5 3.66667C5 3.48242 5.14909 3.33333 5.33333 3.33333H10.6667C10.8509 3.33333 11 3.48242 11 3.66667C11 3.85091 10.8509 4 10.6667 4Z" fill="white"/>
            <path d="M12.6667 0.195107V2.66667H15.1381L12.6667 0.195107Z" fill="white"/>
            <path d="M13.6667 12.5V9.16667C13.6667 8.52266 13.1439 8 12.5 8C0.899451 8 1.65513 8.00076 1.61699 7.99858C1.25946 7.98733 0.933415 7.86407 0.666667 7.66248V11.862C0.666667 12.8542 1.47912 13.6667 2.47145 13.6667H12.5C13.1439 13.6667 13.6667 13.1439 13.6667 12.5ZM5.16667 12C5.2555 12 5.30667 12.0541 5.30667 12.3333C5.30667 12.5176 5.2555 12.6667 5.16667 12.6667C4.24799 12.6667 3.5 11.8878 3.5 11C3.5 10.1122 4.24799 9.33333 5.16667 9.33333C5.2555 9.33333 5.30667 9.39018 5.30667 9.66667C5.30667 9.85091 5.2555 10 5.16667 10C4.61731 10 4.16667 10.4507 4.16667 11C4.16667 11.5493 4.61731 12 5.16667 12ZM7 10.6667H7.33333C7.88269 10.6667 8.33333 11.1173 8.33333 11.6667C8.33333 12.216 7.88269 12.6667 7.33333 12.6667H6.33333C6.14909 12.6667 6 12.5176 6 12.3333C6 12.1491 6.14909 12 6.33333 12H7.33333C7.65661 12 7.66667 11.7613 7.66667 11.6667C7.66667 11.572 7.65661 11.3333 7.33333 11.3333H7C6.45064 11.3333 6 10.8827 6 10.3333C6 9.784 6.45064 9.33333 7 9.33333H8C8.18425 9.33333 8.33333 9.39018 8.33333 9.66667C8.33333 9.85091 8.18425 10 8 10H7C6.67672 10 6.66667 10.2387 6.66667 10.3333C6.66667 10.428 6.67672 10.6667 7 10.6667ZM11.6566 9.77737L10.6138 12.5393C10.5572 12.7075 10.3983 12.6667 10.3333 12.6667C10.2684 12.6667 10.1095 12.7075 10.0529 12.5393L9.01012 9.77737C8.92484 9.51611 9.01195 9.3425 9.15918 9.22442C9.30677 9.10658 9.47682 9.18679 9.56209 9.44805L10.3333 11.396L11.1045 9.44805C11.1898 9.18679 11.3598 9.10658 11.5074 9.22442C11.6547 9.3425 11.7418 9.51611 11.6566 9.77737Z" fill="white"/>
            <path d="M1.66667 7.33333H2.66667V5.33333H1.66667C1.11465 5.33333 0.666667 5.7476 0.666667 6.33333C0.666667 6.91907 1.11465 7.33333 1.66667 7.33333Z" fill="white"/>
        </svg>
    );

    // File Icon Component
    const FileIcon = () => (
        <svg width="11" height="14" viewBox="0 0 11 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6.75714 4.88372C6.49943 4.88372 6.28571 4.66233 6.28571 4.39535V0H2.35714C1.056 0 0 1.09395 0 2.44186V11.5581C0 12.906 1.056 14 2.35714 14H8.64286C9.944 14 11 12.906 11 11.5581V4.88372H6.75714ZM2.98571 3.90698H4.24286C4.50057 3.90698 4.71429 4.12837 4.71429 4.39535C4.71429 4.66233 4.50057 4.88372 4.24286 4.88372H2.98571C2.728 4.88372 2.51429 4.66233 2.51429 4.39535C2.51429 4.12837 2.728 3.90698 2.98571 3.90698ZM8.01429 10.093H2.98571C2.728 10.093 2.51429 9.87163 2.51429 9.60465C2.51429 9.33767 2.728 9.11628 2.98571 9.11628H8.01429C8.272 9.11628 8.48571 9.33767 8.48571 9.60465C8.48571 9.87163 8.272 10.093 8.01429 10.093ZM8.01429 7.48837H2.98571C2.728 7.48837 2.51429 7.26698 2.51429 7C2.51429 6.73302 2.728 6.51163 2.98571 6.51163H8.01429C8.272 6.51163 8.48571 6.73302 8.48571 7C8.48571 7.26698 8.272 7.48837 8.01429 7.48837ZM10.7234 3.90698H7.22857V0.286512L10.7234 3.90698Z" fill={isDark ? '#ffffff' : '#072837'}/>
        </svg>
    );

    // Delete Icon (16x16)
    const DeleteIcon = () => (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2.66602 14.2222C2.66602 15.2045 3.46156 16 4.44381 16H11.5549C12.5371 16 13.3327 15.2045 13.3327 14.2222V3.55554H2.66602V14.2222Z" fill="#FF0000"/>
            <path d="M11.1107 0.888875L10.2218 0H5.77734L4.88843 0.888875H1.77734V2.66667H14.2218V0.888875H11.1107Z" fill="#FF0000"/>
        </svg>
    );

    // Download Icon (14x14)
    const DownloadIcon = () => (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#clip0_3360_1852)">
                <path fillRule="evenodd" clipRule="evenodd" d="M0.996328 9.94006C1.41606 9.94006 1.75633 10.2803 1.75633 10.7001V12.2201C1.75633 12.4216 1.83639 12.6149 1.97894 12.7575C2.12145 12.9 2.31475 12.9801 2.51633 12.9801H11.6363C11.8379 12.9801 12.0311 12.9 12.1737 12.7575C12.3163 12.6149 12.3963 12.4216 12.3963 12.2201V10.7001C12.3963 10.2803 12.7366 9.94006 13.1563 9.94006C13.5761 9.94006 13.9163 10.2803 13.9163 10.7001V12.2201C13.9163 12.8247 13.6761 13.4047 13.2485 13.8323C12.8209 14.2598 12.241 14.5001 11.6363 14.5001H2.51633C1.91165 14.5001 1.3317 14.2599 0.904109 13.8323C0.476549 13.4047 0.236328 12.8247 0.236328 12.2201V10.7001C0.236328 10.2803 0.576588 9.94006 0.996328 9.94006Z" fill="#CD6D2C"/>
                <path fillRule="evenodd" clipRule="evenodd" d="M2.73822 5.60273C3.03502 5.30593 3.51623 5.30593 3.81303 5.60273L7.07561 8.86533L10.3382 5.60273C10.635 5.30593 11.1162 5.30593 11.413 5.60273C11.7099 5.89951 11.7099 6.38074 11.413 6.67752L7.613 10.4775C7.31622 10.7743 6.83499 10.7743 6.53821 10.4775L2.73822 6.67752C2.44143 6.38074 2.44143 5.89951 2.73822 5.60273Z" fill="#CD6D2C"/>
                <path fillRule="evenodd" clipRule="evenodd" d="M7.07641 0.0600586C7.49615 0.0600586 7.83641 0.400326 7.83641 0.820058V9.94006C7.83641 10.3598 7.49615 10.7001 7.07641 10.7001C6.65666 10.7001 6.31641 10.3598 6.31641 9.94006V0.820058C6.31641 0.400326 6.65666 0.0600586 7.07641 0.0600586Z" fill="#CD6D2C"/>
            </g>
            <defs>
                <clipPath id="clip0_3360_1852">
                    <rect width="14" height="14" fill="white"/>
                </clipPath>
            </defs>
        </svg>
    );

    return (
        <>
        {/* Main Container */}
        <div className={`flex flex-col gap-[16px] w-full max-w-[822px] p-[20px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.11)] transition-colors duration-200 ${
            isDark ? 'bg-[#000000] rounded-[10px]' : 'bg-white rounded-[10px]'
        } ${errors.customRecipients ? (isDark ? 'border border-solid border-[#f87171]' : 'border border-solid border-[#FF0000]') : ''}`}>

            {/* Header Row */}
            <div className="flex flex-row justify-between items-start gap-[16px] w-full">
                <span className={`font-semibold text-[16px] leading-[21px] transition-colors duration-200 ${
                    isDark ? 'text-white' : 'text-[#072837]'
                }`} style={{ fontFamily: 'DM Sans' }}>
                    Please add Non-Strata addresses
                </span>

                <button
                    type="button"
                    onClick={downloadTemplate}
                    className="flex flex-row items-center gap-[6px] bg-transparent border-none cursor-pointer hover:opacity-80 transition-opacity"
                >
                    <Download size={14} color="#CD6D2C" />
                    <span className="font-semibold text-[16px] leading-[21px] text-[#CD6D2C]" style={{ fontFamily: 'DM Sans' }}>
                        Download CSV Template
                    </span>
                </button>
            </div>

            {/* Upload CSV Button */}
            <div className="flex flex-row items-center w-full h-[45px]">
                <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex flex-row justify-center items-center p-[12px_16px] gap-[4px] h-[45px] bg-[#CD6D2C] rounded-[12px] border-none cursor-pointer hover:bg-[#B55D24] transition-colors duration-200"
                >
                    <LargeCsvIcon />
                    <span className="font-semibold text-[16px] leading-[21px] text-white" style={{ fontFamily: 'DM Sans' }}>
                        Upload CSV File
                    </span>
                </button>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                />
            </div>

            {/* Success Message */}
            {uploadStatus === 'success' && (
                <div className="flex flex-col items-start p-[10px] gap-[10px] w-full bg-[#D9FBE6] rounded-[5px]">
                    <div className="flex flex-row items-center w-full gap-[2px]">
                        <SmallCsvIcon />
                        <span className="flex-1 font-semibold text-[14px] leading-[18px] tracking-[-0.02em] text-[#0A6E29]" style={{ fontFamily: 'DM Sans' }}>
                            "{fileName}" Imported successfully
                        </span>
                        <button type="button" onClick={clearStatus} className="p-0 border-0 bg-transparent cursor-pointer hover:opacity-70">
                            <X size={10} color="#072837" />
                        </button>
                    </div>
                </div>
            )}

            {/* Uploaded File Display */}
            {fileName && uploadStatus !== 'error' && (
                <div className={`box-border flex flex-col items-start p-[16px_10px] gap-[16px] w-full h-[48px] rounded-[6px] border-solid border-[1px] transition-colors duration-200 ${
                    isDark ? 'bg-[#161515] border-[#292929]' : 'bg-[#F5F5F5] border-[#f8f9fa]'
                }`}>
                    <div className="flex flex-row justify-between items-center w-full h-[16px]">
                        <div className="flex flex-row items-start gap-[4px]">
                            <div className="flex flex-row items-center p-[0px_1px] w-[13px] h-[14px]">
                                <FileIcon />
                            </div>
                            <span className={`font-medium text-[12px] leading-[15px] text-center transition-colors duration-200 ${isDark ? 'text-white' : 'text-[#686868]'}`} style={{ fontFamily: 'Inter' }}>
                                {fileName}
                            </span>
                        </div>
                        <div className="flex flex-row items-center gap-[10px]">
                            <button
                                type="button"
                                onClick={downloadUploadedFile}
                                className="p-0 border-0 bg-transparent cursor-pointer hover:opacity-70"
                            >
                                <DownloadIcon />
                            </button>
                            <button
                                type="button"
                                onClick={handleRemoveFile}
                                className="p-0 border-0 bg-transparent cursor-pointer hover:opacity-70"
                            >
                                <DeleteIcon />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Manual Entry Form */}
            <div className={`flex flex-col items-start p-[20px] gap-[16px] w-full rounded-[10px] transition-colors duration-200 ${
                isDark ? 'bg-[#161515]' : 'bg-[#F5F5F5]'
            }`}>
                <form onSubmit={handleManualSubmit} className="flex flex-col gap-[16px] w-full">
                    {/* Name Field */}
                    <div className="flex flex-col items-start gap-[16px] w-full">
                        <label className={`font-semibold text-[16px] leading-[21px] transition-colors duration-200 ${
                            isDark ? 'text-white' : 'text-[#072837]'
                        }`} style={{ fontFamily: 'DM Sans' }}>
                            Name <span className="text-[#FF0000]">*</span>
                        </label>
                        <div className="flex flex-row items-start w-full">
                            <input
                                type="text"
                                value={manualName}
                                onChange={(e) => setManualName(e.target.value)}
                                placeholder="Enter name"
                                className={`box-border flex flex-row items-center p-[12px] gap-[16px] w-full h-[45px] rounded-[8px] transition-colors duration-200 ${
                                    isDark
                                        ? 'bg-[#000000] text-white placeholder:text-[#686868] border-none focus:outline-none'
                                        : 'bg-white text-[#072837] placeholder:text-[#686868] border-none focus:outline-none'
                                }`}
                                style={{ fontFamily: 'DM Sans', fontSize: '16px', lineHeight: '21px' }}
                            />
                        </div>
                    </div>

                    {/* Postal Field */}
                    <div className="flex flex-col items-start gap-[16px] w-full">
                        <label className={`font-semibold text-[16px] leading-[21px] transition-colors duration-200 ${
                            isDark ? 'text-white' : 'text-[#072837]'
                        }`} style={{ fontFamily: 'DM Sans' }}>
                            Postal <span className="text-[#FF0000]">*</span>
                        </label>
                        <div className="flex flex-row items-start w-full">
                            <input
                                type="text"
                                value={manualPostal}
                                onChange={(e) => setManualPostal(e.target.value)}
                                placeholder="Enter Address"
                                className={`box-border flex flex-row items-center p-[12px] gap-[16px] w-full h-[45px] rounded-[8px] transition-colors duration-200 ${
                                    isDark
                                        ? 'bg-[#000000] text-white placeholder:text-[#686868] border-none focus:outline-none'
                                        : 'bg-white text-[#072837] placeholder:text-[#686868] border-none focus:outline-none'
                                }`}
                                style={{ fontFamily: 'DM Sans', fontSize: '16px', lineHeight: '21px' }}
                            />
                        </div>
                    </div>

                    {manualError && (
                        <div className="flex items-start gap-[8px] text-[14px] font-semibold text-[#FF0000]">
                            <AlertCircle className="w-[16px] h-[16px] flex-shrink-0 mt-[2px]" />
                            <span>{manualError}</span>
                        </div>
                    )}

                    {/* Add Button */}
                    <button
                        type="submit"
                        className="flex flex-row justify-center items-center p-[12px_16px] w-[88px] h-[45px] bg-[#CD6D2C] rounded-[12px] border-none cursor-pointer hover:bg-[#B55D24] transition-colors duration-200"
                    >
                        <span className="font-semibold text-[16px] leading-[21px] text-white" style={{ fontFamily: 'DM Sans' }}>
                            Add
                        </span>
                    </button>
                </form>
            </div>

            {/* View Added Recipients Section */}
            {csvData.length > 0 && (
                <div className={`flex flex-col rounded-[8px] border-solid border-[1px] overflow-hidden ${
                    isDark ? 'border-[#292929]' : 'border-[#d9d9d9]'
                }`}>
                    {/* Collapsible Header */}
                    <button
                        type="button"
                        onClick={() => setShowRecipients(!showRecipients)}
                        className={`flex flex-row items-center justify-between p-[12px] w-full h-[45px] border-none cursor-pointer transition-colors duration-200 ${
                            isDark ? 'bg-[#292929] hover:bg-[#1a1a1a]' : 'bg-[#FFF5EB] hover:bg-[#FFE8D1]'
                        }`}
                    >
                        <span className={`font-medium text-[16px] leading-[21px] flex-1 text-left transition-colors duration-200 ${
                            isDark ? 'text-white' : 'text-[#072837]'
                        }`} style={{ fontFamily: 'DM Sans' }}>
                            View Added Recipients
                        </span>
                        {showRecipients ? (
                            <ChevronUp size={12} className={isDark ? 'text-white' : 'text-[#072837]'} />
                        ) : (
                            <ChevronDown size={12} className={isDark ? 'text-white' : 'text-[#072837]'} />
                        )}
                    </button>

                    {/* Recipients Grid */}
                    {showRecipients && (
                        <div className={`flex flex-row items-center p-[20px] gap-[10px] transition-colors duration-200 ${
                            isDark ? 'bg-[#000000]' : 'bg-white'
                        }`}>
                            <div className="flex flex-col gap-[16px] flex-1">
                                {/* Create rows of 3 cards each */}
                                {Array.from({ length: Math.ceil(csvData.length / 3) }, (_, rowIndex) => (
                                    <div key={rowIndex} className="flex flex-row gap-[16px]">
                                        {csvData.slice(rowIndex * 3, (rowIndex + 1) * 3).map((recipient, colIndex) => {
                                            const index = rowIndex * 3 + colIndex;
                                            return (
                                                <div
                                                    key={index}
                                                    className={`box-border flex flex-row justify-between items-start p-[8px_6px] gap-[8px] w-[232px] h-[74px] rounded-[5px] border-solid border-[1px] transition-colors duration-200 ${
                                                        isDark ? 'bg-[#161515] border-[#292929]' : 'bg-[#F5F5F5] border-[#F5F5F5]'
                                                    }`}
                                                >
                                                    <div className="flex flex-col gap-[4px] flex-1">
                                                        <span className={`font-semibold text-[14px] leading-[18px] tracking-[-0.02em] transition-colors duration-200 ${
                                                            isDark ? 'text-white' : 'text-[#072837]'
                                                        }`} style={{ fontFamily: 'DM Sans' }}>
                                                            {recipient.name}
                                                        </span>
                                                        <span className={`font-normal text-[14px] leading-[18px] tracking-[-0.02em] transition-colors duration-200 ${
                                                            isDark ? 'text-[#b3b3b3]' : 'text-[#686868]'
                                                        }`} style={{ fontFamily: 'DM Sans' }}>
                                                            {recipient.postal}
                                                        </span>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveRecipient(index)}
                                                        className="p-0 border-0 bg-transparent cursor-pointer flex items-center justify-center hover:opacity-70 transition-opacity"
                                                    >
                                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M2.66602 14.2222C2.66602 15.2045 3.46156 16 4.44381 16H11.5549C12.5371 16 13.3327 15.2045 13.3327 14.2222V3.55554H2.66602V14.2222Z" fill="#FF0000"/>
                                                            <path d="M11.1107 0.888875L10.2218 0H5.77734L4.88843 0.888875H1.77734V2.66667H14.2218V0.888875H11.1107Z" fill="#FF0000"/>
                                                        </svg>
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>

        {/* Validation Error message */}
        {errors.customRecipients && (
            <div className={`w-full max-w-[822px] px-[8px] flex items-start gap-[8px] text-[14px] font-semibold ${
                isDark ? 'text-[#f87171]' : 'text-[#FF0000]'
            }`}>
                <AlertCircle className="w-[16px] h-[16px] flex-shrink-0 mt-[2px]" />
                <span>At least one recipient is required</span>
            </div>
        )}
        </>
    );
};

export default CustomRecipientsSection;
